import { ReactElement, useState } from 'react';
import { Component as SkippedIcon } from 'icons/icon-circle--error.svg';
import { parseZone } from 'moment/moment';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { AlertModal, AlertModalVariant } from '../shared/modal/AlertModal';
import { useNotification } from '../../contexts/NotificationContext';
import { Toast, ToastVariant } from '../shared/notification/Toast';
import { Button } from '../shared/button/Button';
import { useUpdateCartSkipMutation } from '../../services/cart/cart.queries';
import styles from './MenuSelector.module.scss';
import { SkipSurveyModal } from '../shared/modal/SkipSurveyModal';
import { useUser } from '../../contexts/UserContext';

type Props = {
  deliveryDate: string;
  isSelectedMenu: boolean;
  menuId: number;
  cartId: number;
  isSkipped: boolean;
  handleSelectMenu: (menuId: number, cartId: number) => void;
  currentMenuDate?: string;
  onClose: () => void;
};

export function MenuSelectorItem({
  deliveryDate,
  isSelectedMenu,
  menuId,
  cartId,
  isSkipped,
  handleSelectMenu,
  currentMenuDate,
  onClose,
}: Props): ReactElement {
  const {
    cart,
    isCartChanged,
    setIsCartChanged,
  } = useMenuWithCart();
  const { isActive } = useUser();
  const {
    showNotification,
    hideNotification,
  } = useNotification();
  const [modalVariant, setModalVariant] = useState<AlertModalVariant | null>(null);
  const [isSurveyOpen, setIsSurveyOpen] = useState(false);
  const [displaySavedCartToast, setDisplaySavedCartToast] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const updateCartSkipMutation = useUpdateCartSkipMutation();

  const currentMenuDateString: string = currentMenuDate
    ? parseZone(currentMenuDate)
      .format('MMMM D')
    : '';

  const skipButtonText = isSkipped ? 'Unskip' : 'Skip';
  const skipButtonHeapId = isSkipped ? 'menu_unskip_button' : 'menu_skip_button';

  // Save cart and navigate to mutated cart menu
  const saveAndNavigate = () => {
    if (displaySavedCartToast) {
      showNotification({
        content: <Toast variant={ToastVariant.CART_SAVED} date={currentMenuDateString} />,
        duration: 5000,
      });
    }
    handleSelectMenu(menuId, cartId);
    onClose();
  };

  const onSelectMenu = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (cart?.priceSummary?.remainingMinimumAmount) {
      setModalVariant(AlertModalVariant.CART_BELOW_MINIMUM);
      hideNotification();
      return;
    }
    if (isCartChanged) {
      setIsCartChanged(false);
      showNotification({
        content: <Toast variant={ToastVariant.CART_SAVED} date={currentMenuDateString} />,
        duration: 5000,
      });
    }
    handleSelectMenu(menuId, cartId);
  };

  const handleSkip = () => {
    if (cart) {
      updateCartSkipMutation.mutate({
        cartId,
        isSkipped,
      });
    }

    if (!isSkipped) {
      setIsSkipping(true);
      setIsSurveyOpen(true);
      return;
    }

    showNotification({
      content: <Toast variant={ToastVariant.CART_UNSKIPPED} />,
      duration: 5000,
    });

    handleSelectMenu(menuId, cartId);
    onClose();
  };

  const handleModalClick = () => {
    setModalVariant(null);
    if (isSkipping) {
      setDisplaySavedCartToast(true);
      setIsSurveyOpen(true);
    } else {
      saveAndNavigate();
    }
  };

  const handleSurveyClose = () => {
    setIsSurveyOpen(false);
    saveAndNavigate();
  };

  const formattedDate: string = parseZone(deliveryDate)
    .format('dddd, MMM D');
  const heapId = `menu_${menuId}`;

  return (
    <>
      <div className={styles.menu_item_container} data-heap-id={heapId}>
        <div
          role="button"
          tabIndex={0}
          className={styles.text_container}
          onClick={onSelectMenu}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleSelectMenu(menuId, cartId);
            }
          }}
        >
          <div className={styles.text_row}>
            <div className={`${styles.text} ${isSelectedMenu ? styles.selected_styling : ''}`}>
              <div
                className={`${styles.text_image_container} ${isSkipped ? styles.skipped_text : ''}`}
              >
                {formattedDate.toString()}
                {(isSelectedMenu && !isSkipped)
                  && <span className={styles.selected_img} />}
                {isSkipped && <SkippedIcon width={16} fill="#91928E" />}
              </div>
            </div>
            {isActive && skipButtonText && (
              <Button
                className={styles.skip_button}
                text={skipButtonText}
                onClick={handleSkip}
                isEnabled
                isSecondary
                heapId={skipButtonHeapId}
              />
            )}
          </div>
        </div>

        {!!modalVariant && (
          <AlertModal
            variant={modalVariant}
            isOpen
            onClose={() => setModalVariant(null)}
            onClick={handleModalClick}
          />
        )}
      </div>

      <SkipSurveyModal
        isOpen={isSurveyOpen}
        onClose={handleSurveyClose}
        menuId={menuId}
      />
    </>
  );
}
